import type { Availability, Court } from '@/types/club.model';
import type { OrganizedMatch, OrganizedMatchType } from '@/types/organized-match.model';
import { formatDateForUser } from '@/utils/formatters';
import { addDays } from '@/utils/localDateOnly';
import { minutesToTime, timeToMinutes } from '@/utils/time';

export type TurnDuration = 60 | 90 | 120;
export const DEFAULT_TURN_DURATION: TurnDuration = 90;

/** Rows start every 30 minutes from opening, so a club opening at 7:00 still offers 7:30. */
const SLOT_STEP_MINUTES = 30;

export type MatchStatus = 'empty' | 'missing' | 'full';

export interface WeekCell {
  date: string;
  time: string;
  canCreate: boolean;
  /** Courts still free in this slot, by sport uid. */
  freeCourts: Record<string, number>;
  matches: OrganizedMatch[];
}

export interface WeekGrid {
  days: { date: string; dayOfWeek: string; label: string }[];
  times: string[];
  cells: Record<string, WeekCell | undefined>;
  /** Rows taken out of the view that are still out, sorted. */
  hiddenTimes: string[];
  /** Rows a match sits on, on some day: they are never hidden nor offered for removal. */
  occupiedTimes: ReadonlySet<string>;
}

export interface WeekGridOptions {
  /**
   * Club-local "yyyy-MM-ddTHH:mm": cells that are not after it cannot be created.
   * Omitted means no cell is considered past.
   */
  now?: string;
  /** Rows the manager added by hand ("HH:mm"); they offer "+" on the days the club opens. */
  extraTimes?: ReadonlySet<string>;
  /** Rows the manager took out of the view. A row holding a match is never hidden. */
  hiddenTimes?: ReadonlySet<string>;
}

export const DAY_NAMES = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];
const DAY_SHORT_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// Calendar arithmetic runs on UTC fields only so the machine timezone never
// shifts a club-local date.
const parseDate = (date: string): number => {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

export function weekStartOf(date: string): string {
  const weekdayFromMonday = (new Date(parseDate(date)).getUTCDay() + 6) % 7;
  return addDays(date, -weekdayFromMonday);
}

export function turnEnd(date: string, time: string, minutes: number): string {
  const total = timeToMinutes(time) + minutes;
  const endDate = addDays(date, Math.floor(total / 1440));
  return `${endDate}T${minutesToTime(total % 1440)}:00`;
}

export function courtCountBySport(courts: Court[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const court of courts) {
    const uid = court.sport?.uid;
    if (uid) counts[uid] = (counts[uid] ?? 0) + 1;
  }
  return counts;
}

/** Every match overlapping the slot holds a court of its sport, not only one starting in it. */
function freeCourtsAt(
  date: string,
  time: string,
  duration: TurnDuration,
  courts: Record<string, number>,
  matches: OrganizedMatch[],
): Record<string, number> {
  const start = `${date}T${time}`;
  const end = turnEnd(date, time, duration).slice(0, 16);
  const free = { ...courts };
  for (const match of matches) {
    const uid = match.sport.uid;
    if (free[uid] && match.startDate.slice(0, 16) < end && match.endDate.slice(0, 16) > start) {
      free[uid] -= 1;
    }
  }
  return free;
}

export function buildWeekGrid(
  availability: Availability[number][],
  weekStart: string,
  duration: TurnDuration,
  matches: OrganizedMatch[],
  // Courts by sport uid: a slot offers "+" while some sport has a court no match holds.
  courts: Record<string, number>,
  { now = '', extraTimes = new Set(), hiddenTimes = new Set() }: WeekGridOptions = {},
): WeekGrid {
  const days = DAY_NAMES.map((dayOfWeek, i) => {
    const date = addDays(weekStart, i);
    return {
      date,
      dayOfWeek,
      label: `${DAY_SHORT_LABELS[i]} ${formatDateForUser(date, 'dd/MM')}`,
    };
  });

  const cells: Record<string, WeekCell | undefined> = {};
  const ensureCell = (date: string, time: string): WeekCell => {
    const key = `${date}T${time}`;
    const cell = cells[key] ?? { date, time, canCreate: false, freeCourts: {}, matches: [] };
    cells[key] = cell;
    return cell;
  };
  const openSlot = (date: string, time: string) => {
    const cell = ensureCell(date, time);
    cell.freeCourts = freeCourtsAt(date, time, duration, courts, matches);
    cell.canCreate = `${date}T${time}` > now && Object.values(cell.freeCourts).some((n) => n > 0);
  };

  for (const day of days) {
    const ranges = availability
      .filter((a) => a.dayOfWeek === day.dayOfWeek)
      .flatMap((a) => a.timeRange ?? []);
    for (const range of ranges) {
      // An end of 00:00 means midnight; a range crossing midnight is clipped to
      // the end of the day because the grid is per day.
      const rawEnd = timeToMinutes(range.endTime);
      const end = rawEnd === 0 || rawEnd < timeToMinutes(range.startTime) ? 1440 : rawEnd;
      for (
        let start = timeToMinutes(range.startTime);
        start + duration <= end;
        start += SLOT_STEP_MINUTES
      ) {
        openSlot(day.date, minutesToTime(start));
      }
    }
    if (ranges.length > 0) extraTimes.forEach((time) => openSlot(day.date, time));
  }

  for (const match of matches) {
    const date = match.startDate.slice(0, 10);
    if (!days.some((d) => d.date === date)) continue;
    ensureCell(date, match.startDate.slice(11, 16)).matches.push(match);
  }

  const axis = [...new Set(Object.values(cells).map((c) => c?.time ?? ''))].sort();
  const occupiedTimes = new Set(
    Object.values(cells).flatMap((c) => (c?.matches.length ? [c.time] : [])),
  );
  const isHidden = (time: string) => hiddenTimes.has(time) && !occupiedTimes.has(time);
  return {
    days,
    times: axis.filter((time) => !isHidden(time)),
    cells,
    hiddenTimes: axis.filter(isHidden),
    occupiedTimes,
  };
}

export const filled = (match: OrganizedMatch): number =>
  match.team1.players.length + match.team2.players.length;

export const capacity = (match: OrganizedMatch): number => match.playersPerTeam * 2;

export function matchStatus(match: OrganizedMatch): MatchStatus {
  const count = filled(match);
  if (count === 0) return 'empty';
  return count < capacity(match) ? 'missing' : 'full';
}

export function matchStatusLabel(status: MatchStatus): string {
  switch (status) {
    case 'empty':
      return 'Vacío';
    case 'missing':
      return 'Faltan jugadores';
    case 'full':
      return 'Completo';
  }
}

export const matchTypeLabel = (type: OrganizedMatchType): string =>
  type === 'COMPETITIVE' ? 'Competitivo' : 'Amistoso';

export function matchSummary(match: OrganizedMatch): string {
  const categoryName = match.category?.name;
  const type =
    match.matchType === 'COMPETITIVE' && categoryName
      ? `Comp. ${categoryName}`
      : matchTypeLabel(match.matchType);
  return `${filled(match)}/${capacity(match)} · ${type}`;
}
